import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useSubscription } from '@/context/SubscriptionContext';
import { CheckCircle, Loader2 } from 'lucide-react';

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkCheckoutStatus } = useSubscription();
  const [status, setStatus] = useState('checking');
  // Use a ref for the polling counter so the effect doesn't re-spawn
  // a new poll cycle each time `attempts` increments. The previous
  // setAttempts-in-deps pattern double-counted on every render.
  const attemptsRef = useRef(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      navigate('/subscription');
      return;
    }

    let cancelled = false;
    // Reset polling counter on every mount so React-strict-mode double-mount
    // (or a fast re-mount via back/forward navigation) starts from 0.
    attemptsRef.current = 0;
    const pollStatus = async () => {
      if (cancelled) return;
      try {
        const result = await checkCheckoutStatus(sessionId);
        if (cancelled) return;
        
        if (result.payment_status === 'paid') {
          setStatus('success');
          try {
            const { track } = await import('@/lib/analytics');
            track.subscriptionStarted(
              result.metadata?.plan || 'vip',
              Number(result.amount_total ? result.amount_total / 100 : (result.value || 0))
            );
          } catch (analyticsErr) {
            console.debug('[analytics] subscriptionStarted failed:', analyticsErr);
          }
        } else if (result.status === 'expired') {
          setStatus('expired');
        } else if (attemptsRef.current < 5) {
          attemptsRef.current += 1;
          setTimeout(pollStatus, 2000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        console.error('Error checking status:', error);
        if (!cancelled) setStatus('error');
      }
    };

    pollStatus();
    return () => { cancelled = true; };
  }, [searchParams, checkCheckoutStatus, navigate]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container py-12">
        <div className="max-w-md mx-auto">
          <Card>
            <CardContent className="py-12 text-center">
              {status === 'checking' && (
                <>
                  <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
                  <h2 className="text-xl font-bold mb-2">Processing Payment</h2>
                  <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
                </>
              )}
              
              {status === 'success' && (
                <>
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                  <h2 className="text-xl font-bold mb-2">Payment Successful!</h2>
                  <p className="text-muted-foreground mb-6">
                    Thank you for subscribing to ClearCommunication Premium. You now have unlimited access to all phrases!
                  </p>
                  <Button onClick={() => navigate('/')}>
                    Start Exploring
                  </Button>
                </>
              )}
              
              {status === 'pending' && (
                <>
                  <Loader2 className="h-12 w-12 mx-auto mb-4 text-yellow-500" />
                  <h2 className="text-xl font-bold mb-2">Payment Processing</h2>
                  <p className="text-muted-foreground mb-6">
                    Your payment is being processed. This may take a few moments. You'll receive a confirmation email shortly.
                  </p>
                  <Button onClick={() => navigate('/')}>
                    Go to Home
                  </Button>
                </>
              )}
              
              {status === 'expired' && (
                <>
                  <div className="h-12 w-12 mx-auto mb-4 text-red-500">❌</div>
                  <h2 className="text-xl font-bold mb-2">Session Expired</h2>
                  <p className="text-muted-foreground mb-6">
                    Your payment session has expired. Please try again.
                  </p>
                  <Button onClick={() => navigate('/subscription')}>
                    Try Again
                  </Button>
                </>
              )}
              
              {status === 'error' && (
                <>
                  <div className="h-12 w-12 mx-auto mb-4 text-red-500">❌</div>
                  <h2 className="text-xl font-bold mb-2">Something Went Wrong</h2>
                  <p className="text-muted-foreground mb-6">
                    We couldn't verify your payment. Please contact support if you were charged.
                  </p>
                  <Button onClick={() => navigate('/subscription')}>
                    Back to Subscription
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;
